import {
  collection,
  query,
  orderBy,
  where,
  getDocs,
  documentId,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

export type StreakStatus = "new" | "active" | "warning" | "lost";

export interface DayStreakInfo {
  /** YYYYMMDD */
  dateID: string;
  /** Total number of log entries across all log types */
  totalLogs: number;
  /** Intensity score for heatmap dot colour (0–7) */
  intensity: number;
  /** Peak score for this day (undefined if no peak score log exists) */
  peakScore?: number;
}

export interface StreakResult {
  /** Consecutive logged days from today backward (stops at first gap) */
  currentStreak: number;
  /** Calendar days since the most recent logged day (0 = today, 1 = yesterday …) */
  daysSinceLastLog: number;
  /** Overall state of the streak */
  status: StreakStatus;
  /** Last 7 days of info for the mini dot row (index 0 = 6 days ago, 6 = today) */
  last7Days: DayStreakInfo[];
  /** The streak count just before it was lost (for "Your X-day streak ended" copy) */
  previousStreakBeforeLost: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toDateID(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Count the total number of log IDs across all 8 log types for one dayPost doc.
 * stepLogs and noteLogs count just like everything else for the streak.
 */
function countTotalLogs(data: any): number {
  return (
    (data.trainingLogIDs?.length ?? 0) +
    (data.recoveryLogIDs?.length ?? 0) +
    (data.niggleLogIDs?.length ?? 0) +
    (data.performanceLogIDs?.length ?? 0) +
    (data.healthLogIDs?.length ?? 0) +
    (data.stepLogIDs?.length ?? 0) +
    (data.noteLogIDs?.length ?? 0) +
    (data.wellnessLogIDs?.length ?? 0)
  );
}

/**
 * Derive a 0-7 intensity score for heatmap dot colouring.
 * stepLogs and noteLogs score 0, niggle/health/wellness cap at 1.
 */
function calcIntensity(data: any): number {
  let score = 0;
  score += data.trainingLogIDs?.length ?? 0;
  score += data.recoveryLogIDs?.length ?? 0;
  score += data.performanceLogIDs?.length ?? 0;
  if ((data.niggleLogIDs?.length ?? 0) > 0) score += 1;
  if ((data.healthLogIDs?.length ?? 0) > 0) score += 1;
  if ((data.wellnessLogIDs?.length ?? 0) > 0) score += 1;
  return Math.min(score, 7);
}

// ---------------------------------------------------------------------------
// Batch query helper
// ---------------------------------------------------------------------------

const BATCH_SIZE = 30; // docs per Firestore query

/**
 * Fetch a window of dayPost docs between two dateIDs (inclusive).
 * Returns a Map: dateID → { totalLogs, intensity, peakScoreLogID? }
 */
async function fetchWindow(
  userId: string,
  startID: string,
  endID: string,
): Promise<
  Map<string, { totalLogs: number; intensity: number; peakScoreLogID?: string }>
> {
  const postsRef = collection(db, "dayPosts", userId, "posts");
  const q = query(
    postsRef,
    orderBy(documentId()),
    where(documentId(), ">=", startID),
    where(documentId(), "<=", endID),
  );
  const snapshot = await getDocs(q);
  const result = new Map<
    string,
    { totalLogs: number; intensity: number; peakScoreLogID?: string }
  >();
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    result.set(docSnap.id, {
      totalLogs: countTotalLogs(data),
      intensity: calcIntensity(data),
      peakScoreLogID: data.peakScoreLogID ?? undefined,
    });
  });
  return result;
}

/**
 * Given a fetchWindow result map, batch-fetch peak scores for entries that have a peakScoreLogID.
 * Returns a Map: dateID → peakScore (number).
 */
async function fetchPeakScoresForWindow(
  userId: string,
  windowMap: Map<
    string,
    { totalLogs: number; intensity: number; peakScoreLogID?: string }
  >,
): Promise<Map<string, number>> {
  const peakScores = new Map<string, number>();
  const promises: Promise<void>[] = [];

  windowMap.forEach((entry, dateID) => {
    if (entry.peakScoreLogID) {
      const logID = entry.peakScoreLogID;
      promises.push(
        getDoc(doc(db, "peakScoreLogs", userId, "logs", logID))
          .then((snap) => {
            if (snap.exists()) {
              const score = snap.data()?.peakScore;
              if (typeof score === "number") {
                peakScores.set(dateID, score);
              }
            }
          })
          .catch(() => {
            // Silently ignore permission or fetch errors for peak scores
          }),
      );
    }
  });

  await Promise.all(promises);
  return peakScores;
}

// ---------------------------------------------------------------------------
// Full streak calculation (batched, no cap)
// ---------------------------------------------------------------------------

/**
 * Walk backward from today in BATCH_SIZE-day windows until we find a gap
 * (a day with no logs) or reach the minimum date (Jan 1 of two years ago).
 *
 * Returns: { currentStreak, lastLoggedDateID, logMap (last 7 days) }
 *
 * Firestore cost: ceil(streak / BATCH_SIZE) queries. For a 63-day streak
 * that's 3 queries. For a 1000-day streak that's ~34 queries — done once,
 * then cached.
 */
export async function fetchStreakFull(userId: string): Promise<StreakResult> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Earliest date we will look back to (Jan 1, two years ago)
  const minDate = new Date(today.getFullYear() - 1, 0, 1);
  minDate.setHours(0, 0, 0, 0);

  let currentStreak = 0;
  let daysSinceLastLog = -1;
  let gapFound = false;

  // Merged log map — we keep populating as we walk backward
  const logMap = new Map<string, { totalLogs: number; intensity: number }>();

  let batchEnd = today;

  while (!gapFound && batchEnd >= minDate) {
    // Start of this batch window
    const batchStart = addDays(batchEnd, -(BATCH_SIZE - 1));
    const effectiveStart = batchStart < minDate ? minDate : batchStart;

    const startID = toDateID(effectiveStart);
    const endID = toDateID(batchEnd);

    const window = await fetchWindow(userId, startID, endID);

    // Merge into master logMap (for last7Days construction later)
    window.forEach((v, k) => logMap.set(k, v));

    // Walk backward through this batch day by day
    let cursor = batchEnd;
    while (cursor >= effectiveStart) {
      const dateID = toDateID(cursor);
      const entry = window.get(dateID);
      const logged = (entry?.totalLogs ?? 0) > 0;
      const offset = Math.round(
        (today.getTime() - cursor.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysSinceLastLog === -1 && logged) {
        daysSinceLastLog = offset;
      }

      if (logged) {
        if (offset === currentStreak) {
          currentStreak++;
        } else if (daysSinceLastLog !== -1) {
          // Gap encountered — streak is broken
          gapFound = true;
          break;
        }
      } else if (daysSinceLastLog !== -1) {
        // This day has no logs and we've already seen a logged day → gap
        gapFound = true;
        break;
      }

      cursor = addDays(cursor, -1);
    }

    // Move to next batch window
    batchEnd = addDays(effectiveStart, -1);

    // If we reached the minimum date without a gap, stop
    if (effectiveStart <= minDate) break;
  }

  // Never found any logged day at all
  if (daysSinceLastLog === -1) {
    daysSinceLastLog = 10;
  }

  // ── Previous streak before lost ──────────────────────────────────────────
  let previousStreakBeforeLost = 0;
  if (daysSinceLastLog >= 7) {
    const entries: boolean[] = [];
    for (let offset = 9; offset >= 0; offset--) {
      const dateID = toDateID(addDays(today, -offset));
      entries.push((logMap.get(dateID)?.totalLogs ?? 0) > 0);
    }
    let run = 0;
    for (const logged of entries) {
      if (logged) run++;
      else {
        previousStreakBeforeLost = Math.max(previousStreakBeforeLost, run);
        run = 0;
      }
    }
    previousStreakBeforeLost = Math.max(previousStreakBeforeLost, run);
  }

  // ── Status ────────────────────────────────────────────────────────────────
  let status: StreakStatus;
  if (currentStreak === 0 && daysSinceLastLog >= 10) {
    status = "new";
  } else if (daysSinceLastLog >= 7) {
    status = "lost";
  } else if (daysSinceLastLog >= 5) {
    status = "warning";
  } else {
    status = "active";
  }

  // ── Last 7 days dot row — always a cheap 7-doc query for freshness ────────
  const last7StartID = toDateID(addDays(today, -6));
  const last7Map = await fetchWindow(userId, last7StartID, toDateID(today));
  const last7PeakScores = await fetchPeakScoresForWindow(userId, last7Map);

  const last7Days: DayStreakInfo[] = [];
  for (let offset = 6; offset >= 0; offset--) {
    const date = addDays(today, -offset);
    const dateID = toDateID(date);
    const entry = last7Map.get(dateID);
    last7Days.push({
      dateID,
      totalLogs: entry?.totalLogs ?? 0,
      intensity: entry?.intensity ?? 0,
      peakScore: last7PeakScores.get(dateID),
    });
  }

  return {
    currentStreak,
    daysSinceLastLog,
    status,
    last7Days,
    previousStreakBeforeLost,
  };
}

// ---------------------------------------------------------------------------
// Cheap daily re-verification (Option C — same-day check)
// ---------------------------------------------------------------------------

/**
 * Given a known streak count from a previous session, verify it is still
 * valid for today. Costs exactly 2 Firestore reads:
 *   1. Today's dayPost (is today logged?)
 *   2. Yesterday's dayPost (is the chain still intact?)
 * Plus 1 separate 7-doc query for the dot row.
 *
 * Returns a full StreakResult so the hook interface stays unchanged.
 */
export async function fetchStreakIncremental(
  userId: string,
  previousStreak: number,
  lastLoggedDateID: string,
): Promise<StreakResult> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = addDays(today, -1);
  const todayID = toDateID(today);
  const yesterdayID = toDateID(yesterday);

  // Fetch today + yesterday in one range query (2 docs max)
  const recentMap = await fetchWindow(userId, yesterdayID, todayID);

  const todayLogged = (recentMap.get(todayID)?.totalLogs ?? 0) > 0;
  const yesterdayLogged = (recentMap.get(yesterdayID)?.totalLogs ?? 0) > 0;

  let currentStreak = previousStreak;
  let daysSinceLastLog: number;

  if (todayLogged) {
    // Today is logged
    daysSinceLastLog = 0;
    // If yesterday was also logged (or streak was already counting yesterday),
    // the chain continues. If yesterday was NOT logged and previousStreak > 0
    // it means the chain already included a gap — need a full recount.
    if (lastLoggedDateID === todayID) {
      // Already counted today in a previous call this session — no change
    } else if (lastLoggedDateID === yesterdayID || previousStreak === 0) {
      // Chain extends by 1
      currentStreak = previousStreak + 1;
    } else {
      // lastLoggedDate is older than yesterday — there was a gap
      // Fall back to full scan to get the correct number
      return fetchStreakFull(userId);
    }
  } else if (yesterdayLogged) {
    daysSinceLastLog = 1;
    // Check if last known logged date matches yesterday — chain intact
    if (lastLoggedDateID !== yesterdayID) {
      return fetchStreakFull(userId);
    }
  } else {
    // Neither today nor yesterday logged — need to find where we actually are
    return fetchStreakFull(userId);
  }

  // ── Status ────────────────────────────────────────────────────────────────
  let status: StreakStatus;
  if (currentStreak === 0 && daysSinceLastLog! >= 10) {
    status = "new";
  } else if (daysSinceLastLog! >= 7) {
    status = "lost";
  } else if (daysSinceLastLog! >= 5) {
    status = "warning";
  } else {
    status = "active";
  }

  // ── Last 7 days dot row ──────────────────────────────────────────────────
  const last7StartID = toDateID(addDays(today, -6));
  const last7Map = await fetchWindow(userId, last7StartID, todayID);
  const last7PeakScores = await fetchPeakScoresForWindow(userId, last7Map);

  const last7Days: DayStreakInfo[] = [];
  for (let offset = 6; offset >= 0; offset--) {
    const date = addDays(today, -offset);
    const dateID = toDateID(date);
    const entry = last7Map.get(dateID);
    last7Days.push({
      dateID,
      totalLogs: entry?.totalLogs ?? 0,
      intensity: entry?.intensity ?? 0,
      peakScore: last7PeakScores.get(dateID),
    });
  }

  return {
    currentStreak,
    daysSinceLastLog: daysSinceLastLog!,
    status,
    last7Days,
    previousStreakBeforeLost: 0,
  };
}

// ---------------------------------------------------------------------------
// Public entry point (kept for backward compatibility)
// ---------------------------------------------------------------------------

/**
 * @deprecated Use fetchStreakFull / fetchStreakIncremental via useStreakData.
 * Kept so any external callers don't break during migration.
 */
export async function fetchStreakData(userId: string): Promise<StreakResult> {
  return fetchStreakFull(userId);
}
